// SCR-STAFF-1005 — اتصال ارجاع ملی و سیب (AP-MED-03؛ US-INT-100، 102): PID اختصاصی نرم‌افزار و شناسه مرکز هر شعبه،
// آزمون اتصال و نمای همگام‌سازی ظرفیت ارجاعی شعب.
//
// این صفحه **روی** SCR-STAFF-1000 می‌نشیند: ساخت و ویرایش اتصال همان‌جاست و این‌جا فقط اتصال‌های خانواده ارجاع ملی،
// وضعیت فعال‌سازی (`PHX-INT-0300` بدون PID)، نتیجه آزمون (`PHX-INT-0303` رد PID) و عقب‌ماندگی ۲۰ دقیقه دیده می‌شوند.
// اعتبارنامه مرکز هرگز نمایش داده نمی‌شود؛ PID پیکربندی غیرمحرمانه است (AC-INT-100.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ConnectionTestView,
  type IntegrationEndpointView,
  type SchReferralCapacityHealthResponse,
  type SchReferralCapacityHealthView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  JalaliDate,
  ListPage,
  SampleBadge,
  faDigits,
  toJalaliDateTime,
  useDisplaySettings,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { intKeys } from './common';

export const SCR_STAFF_1005 = 'SCR-STAFF-1005';

/** خانواده Adapter ارجاع ملی (Port ارجاع و Port ظرفیت). */
const REFERRAL_ADAPTER_PREFIX = 'INT-NATIONAL-REFERRAL';

/** تنظیم غیرمحرمانه اتصال؛ مقدار تهی یعنی فعال‌سازی `PHX-INT-0300` می‌گیرد. */
function setting(endpoint: IntegrationEndpointView, key: string): string | null {
  const settings = endpoint.settings as Record<string, unknown> | null | undefined;
  const value = settings?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function NationalReferralConnectionPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<{ endpointId: string; result: ConnectionTestView } | null>(null);
  const canManage = access.can('int.endpoint.manage');
  const canViewHealth = access.can('sch.referral_capacity.send');

  const endpoints = useQuery({
    queryKey: intKeys.endpoints(session?.tenantId),
    enabled: canManage,
    queryFn: async (): Promise<IntegrationEndpointView[]> => {
      const response = await unwrap(tenantApi.GET('/api/v1/integration/endpoints', {}));
      return ((response as { items?: IntegrationEndpointView[] }).items ?? []).filter((item) =>
        (item.adapterCode ?? '').startsWith(REFERRAL_ADAPTER_PREFIX),
      );
    },
  });

  const health = useQuery({
    queryKey: ['scheduling', session?.tenantId, 'referral-health'] as const,
    enabled: canViewHealth,
    queryFn: async (): Promise<SchReferralCapacityHealthView[]> => {
      const response = await unwrap(tenantApi.GET('/api/v1/scheduling/referral-capacity/health', {}));
      return (response as SchReferralCapacityHealthResponse).items;
    },
  });

  const test = useMutation({
    mutationFn: async (endpointId: string) => {
      const result = (await unwrap(
        tenantApi.POST('/api/v1/integration/endpoints/{id}:test', { params: { path: { id: endpointId } } }),
      )) as ConnectionTestView;
      return { endpointId, result };
    },
    onSuccess: (value) => {
      setTestResult(value);
      void queryClient.invalidateQueries({ queryKey: intKeys.endpoints(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<IntegrationEndpointView>[] = [
    {
      id: 'adapter',
      headerKey: 'int:national_referral.adapter',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`int:adapter.${row.adapterCode}`, { defaultValue: row.adapterCode ?? '—' })}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    {
      id: 'branch',
      headerKey: 'int:national_referral.branch',
      cell: (row) => <span>{row.branchId ? faDigits(row.branchId.slice(0, 8)) : t('int:national_referral.tenant_level')}</span>,
    },
    {
      id: 'pid',
      headerKey: 'int:national_referral.pid',
      cell: (row) => (
        <span data-testid={`int-referral-pid-${row.id}`}>
          {setting(row, 'pid') ?? t('int:national_referral.pid_missing')}
        </span>
      ),
    },
    {
      id: 'center',
      headerKey: 'int:national_referral.center_id',
      cell: (row) => <span>{setting(row, 'center_id') ?? t('int:national_referral.pid_missing')}</span>,
    },
    {
      id: 'active',
      headerKey: 'int:national_referral.active',
      cell: (row) => (
        <Chip
          size="small"
          color={row.isActive ? 'success' : 'default'}
          label={row.isActive ? t('int:national_referral.active_yes') : t('int:national_referral.active_no')}
          data-testid={`int-referral-active-${row.id}`}
        />
      ),
    },
    {
      id: 'test',
      headerKey: 'int:national_referral.last_test',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span data-testid={`int-referral-test-${row.id}`} data-ok={String(row.lastTestOk ?? '')}>
            {row.lastTestOk === null || row.lastTestOk === undefined
              ? t('int:connections.not_tested')
              : row.lastTestOk
                ? t('int:connections.test_ok')
                : t('int:connections.test_failed', { code: row.lastTestCode ?? '—' })}
          </span>
          {row.lastTestAt ? (
            <Typography variant="caption" color="text.secondary">
              <JalaliDate value={row.lastTestAt} withTime />
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'int:national_referral.test',
      cell: (row) => (
        <Button size="small" variant="outlined" onClick={() => test.mutate(row.id)} data-testid={`int-referral-test-run-${row.id}`}>
          {t('int:national_referral.test')}
        </Button>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1005}
      titleKey="int:national_referral.page_title"
      subtitleKey="int:national_referral.page_subtitle"
      actions={
        canManage ? (
          <Button component={RouterLink} to="/settings/integrations" variant="outlined" data-testid="int-referral-connections-link">
            {t('int:national_referral.manage_connections')}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={3}>
        <Alert severity="info" data-testid="int-referral-pid-hint">
          {t('int:national_referral.pid_hint')}
        </Alert>
        {testResult && !testResult.result.ok ? (
          <Alert severity="error" data-testid="int-referral-test-failed">
            {t('int:national_referral.test_failed', { code: testResult.result.resultCode ?? '—' })}
          </Alert>
        ) : null}
        <DataTable
          rows={endpoints.data}
          columns={columns}
          getRowId={(row) => row.id}
          isLoading={endpoints.isLoading}
          error={endpoints.error}
          onRetry={() => void endpoints.refetch()}
          emptyTitleKey="int:national_referral.empty"
          captionKey="int:national_referral.page_title"
        />
        {canViewHealth ? (
          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('int:national_referral.sync_title')}</Typography>
            <Alert severity="info" data-testid="int-referral-sla-hint">
              {t('int:national_referral.sla_hint')}
            </Alert>
            <DataTable
              rows={health.data}
              columns={[
                {
                  id: 'branch',
                  headerKey: 'int:national_referral.branch',
                  cell: (row) => <span>{faDigits(row.branchId.slice(0, 8))}</span>,
                },
                {
                  id: 'last',
                  headerKey: 'int:national_referral.last_push',
                  cell: (row) => <span>{row.lastSuccessfulPushAt ? toJalaliDateTime(row.lastSuccessfulPushAt, { timeZone }) : '—'}</span>,
                },
                {
                  id: 'oldest',
                  headerKey: 'int:national_referral.oldest_unsent',
                  cell: (row) => (
                    <span data-testid={`int-referral-oldest-${row.branchId}`}>
                      {row.oldestUnsentAt ? toJalaliDateTime(row.oldestUnsentAt, { timeZone }) : '—'}
                    </span>
                  ),
                },
                {
                  id: 'notsent',
                  headerKey: 'int:national_referral.not_sent',
                  cell: (row) => <span>{faDigits(row.notSentCount)}</span>,
                },
                {
                  id: 'error',
                  headerKey: 'int:national_referral.last_error',
                  cell: (row) => <span>{row.lastErrorCode ?? '—'}</span>,
                },
              ]}
              getRowId={(row) => row.branchId}
              isLoading={health.isLoading}
              error={health.error}
              onRetry={() => void health.refetch()}
              emptyTitleKey="int:national_referral.sync_empty"
              captionKey="int:national_referral.sync_title"
            />
          </Stack>
        ) : null}
      </Stack>
    </ListPage>
  );
}
